import { AlertTriangle } from 'lucide-react'
import type { RosterMember } from '@/types/rosterTypes'
import { MobileStatus } from './MobileStatus'
import { getInitials } from '@/lib/format'
import { cn } from '@/lib/utils'

interface RosterListProps {
  members: Array<RosterMember>
  /**
   * Show where each person came from. Off for a group with no overrides, where
   * every row would read "from rule" — a column that says the same thing on
   * every line is noise, and it would bury the exceptions on the groups that
   * do have them.
   */
  showSource?: boolean
  /** Excluded people are shown greyed out: they are context, not recipients. */
  muted?: boolean
  emptyMessage?: string
  className?: string
}

/**
 * A list of people on a group's roster. Shared by the Groups tab and the
 * recipient field's group dropdown, so "who will actually get this" reads the
 * same whether you're browsing groups or about to hit send.
 */
export function RosterList({
  members,
  showSource = false,
  muted = false,
  emptyMessage = 'Nobody here.',
  className,
}: RosterListProps) {
  if (members.length === 0) {
    return <div className="px-4 py-6 text-center text-[13px] text-faint">{emptyMessage}</div>
  }

  return (
    <ul className={cn('divide-y divide-border-2', className)}>
      {members.map((member) => (
        <li
          key={member.id}
          className={cn('flex items-center gap-[11px] px-[13px] py-2.5', muted && 'opacity-70')}
        >
          <span
            className={cn(
              'flex h-8 w-8 flex-none items-center justify-center rounded-full text-[11px] font-bold',
              member.hasMobile
                ? 'bg-primary-tint text-primary'
                : 'bg-warn-tint text-warn-ink',
            )}
          >
            {getInitials(member.displayName)}
          </span>

          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13.5px] font-bold text-foreground">
              {member.displayName}
            </span>
            <span className="flex min-w-0 items-center gap-1.5">
              <MobileStatus hasMobile={member.hasMobile} className="flex-none text-[11.5px]" />
              {member.jobTitle && (
                <>
                  <span className="flex-none text-[11.5px] text-faint">·</span>
                  <span className="truncate text-[11.5px] text-muted-foreground">
                    {member.jobTitle}
                  </span>
                </>
              )}
            </span>
          </span>

          {showSource && <SourceBadge member={member} />}
        </li>
      ))}
    </ul>
  )
}

/** Why this person is — or isn't — on the roster. */
function SourceBadge({ member }: { member: RosterMember }) {
  const base =
    'inline-flex flex-none items-center gap-1 rounded-[6px] px-2 py-[3px] text-[10.5px] font-extrabold uppercase tracking-[0.03em]'

  if (member.source === 'excluded') {
    // An exclusion naming somebody who was never in the group did nothing at
    // all. That's almost always the wrong ".Exclude" group, and it would
    // otherwise look every bit as deliberate as a real one.
    if (!member.origin) {
      return (
        <span className={cn(base, 'bg-warn-tint text-warn-ink')}>
          <AlertTriangle className="h-[11px] w-[11px]" />
          Not in this group
        </span>
      )
    }
    return (
      <span className={cn(base, 'bg-surface-2 text-muted-foreground')}>
        {member.origin === 'added' ? 'Added, then excluded' : 'Excluded'}
      </span>
    )
  }

  if (member.source === 'added') {
    return <span className={cn(base, 'bg-teal-soft text-teal')}>Added</span>
  }

  // The unremarkable case, kept quiet so the overrides stand out beside it.
  return <span className={cn(base, 'font-bold normal-case text-faint')}>From rule</span>
}
